import type { Order, OrderItem } from "./api";

function formatMoney(minor: number | null | undefined): string {
  if (minor == null) return "0.00";
  return (minor / 100).toFixed(2);
}

// Provider- and customer-supplied strings end up inside an HTML document we
// write into a print iframe, so every one of them must be escaped.
function esc(value: unknown): string {
  if (value == null) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function buildReceiptHTML(order: Order): string {
  const currency = esc(order.currency ?? "PLN");
  const placed = esc(new Date(order.placed_at).toLocaleString());
  const displayCode = esc(order.display_code ?? order.external_order_id);
  const customerName = esc(
    order.customer?.name
      || [order.customer?.first_name, order.customer?.last_name].filter(Boolean).join(" ")
      || "—",
  );

  const itemsHTML = (order.items ?? [])
    .map(
      (item: OrderItem) => `
      <tr>
        <td style="padding:4px 0">${item.quantity}× ${esc(item.name)}</td>
        <td style="padding:4px 0;text-align:right">${formatMoney(item.total_price_minor)} ${currency}</td>
      </tr>
      ${item.modifiers?.length ? item.modifiers.map(m => `
        <tr><td style="padding:0 0 0 16px;font-size:11px;color:#666">+ ${esc(m.name)}${m.quantity > 1 ? ` ×${esc(m.quantity)}` : ""}</td><td></td></tr>
      `).join("") : ""}
      ${item.notes ? `<tr><td style="padding:0 0 0 16px;font-size:11px;color:#b8860b">📝 ${esc(item.notes)}</td><td></td></tr>` : ""}
    `,
    )
    .join("");

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Order #${displayCode}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Courier New', monospace; width: 80mm; padding: 8mm; font-size: 12px; color: #000; }
        .header { text-align: center; border-bottom: 2px dashed #000; padding-bottom: 8px; margin-bottom: 8px; }
        .header h1 { font-size: 18px; margin-bottom: 4px; }
        .header .code { font-size: 22px; font-weight: bold; margin: 6px 0; }
        .meta { margin-bottom: 8px; }
        .meta div { padding: 2px 0; }
        table { width: 100%; border-collapse: collapse; }
        .divider { border-top: 1px dashed #000; margin: 6px 0; }
        .total { font-size: 16px; font-weight: bold; text-align: right; padding-top: 6px; }
        .notes { background: #f5f5f5; padding: 6px; margin: 8px 0; border-radius: 4px; font-size: 11px; }
        .footer { text-align: center; margin-top: 12px; font-size: 10px; color: #666; border-top: 2px dashed #000; padding-top: 8px; }
        @media print { body { width: auto; } }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>RESTIFY</h1>
        <div class="code">#${displayCode}</div>
        <div style="text-transform:capitalize">${esc(order.platform.replace("_", " "))}</div>
      </div>
      <div class="meta">
        <div><strong>Date:</strong> ${placed}</div>
        <div><strong>Customer:</strong> ${customerName}</div>
        ${order.customer?.phone ? `<div><strong>Phone:</strong> ${esc(order.customer.phone)}</div>` : ""}
        <div><strong>Type:</strong> ${order.order_type === "delivery" ? "🚗 Delivery" : "🏪 Takeaway"}</div>
      </div>
      ${order.customer_notes ? `<div class="notes"><strong>Note:</strong> ${esc(order.customer_notes)}</div>` : ""}
      <div class="divider"></div>
      <table>${itemsHTML}</table>
      <div class="divider"></div>
      <table>
        <tr><td>Subtotal</td><td style="text-align:right">${formatMoney(order.totals.subtotal_minor)} ${currency}</td></tr>
        ${order.totals.delivery_fee_minor ? `<tr><td>Delivery</td><td style="text-align:right">${formatMoney(order.totals.delivery_fee_minor)} ${currency}</td></tr>` : ""}
        ${order.totals.tip_minor ? `<tr><td>Tip</td><td style="text-align:right">${formatMoney(order.totals.tip_minor)} ${currency}</td></tr>` : ""}
        ${order.totals.discount_minor ? `<tr><td>Discount</td><td style="text-align:right">-${formatMoney(order.totals.discount_minor)} ${currency}</td></tr>` : ""}
      </table>
      <div class="divider"></div>
      <div class="total">TOTAL: ${formatMoney(order.totals.total_minor)} ${currency}</div>
      <div class="footer">
        <div>Thank you! — RESTIFY</div>
        <div>${esc(new Date().toISOString().slice(0, 19).replace("T", " "))}</div>
      </div>
    </body>
    </html>
  `;
}

export function printOrder(order: Order) {
  const html = buildReceiptHTML(order);
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "none";
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument ?? iframe.contentWindow?.document;
  if (!doc) return;
  doc.open();
  doc.write(html);
  doc.close();

  iframe.contentWindow?.focus();
  iframe.contentWindow?.print();
  setTimeout(() => document.body.removeChild(iframe), 2000);
}

export function printTestPage() {
  const html = `
    <!DOCTYPE html>
    <html>
    <head><meta charset="utf-8"><title>Test Print</title>
    <style>
      body { font-family: 'Courier New', monospace; width: 80mm; padding: 8mm; text-align: center; }
      h1 { font-size: 20px; margin-bottom: 8px; }
      .line { border-top: 2px dashed #000; margin: 12px 0; }
    </style></head>
    <body>
      <h1>RESTIFY</h1>
      <div class="line"></div>
      <p>Test Print Successful</p>
      <p>${esc(new Date().toLocaleString())}</p>
      <div class="line"></div>
      <p style="font-size:10px">Printer is configured correctly.</p>
    </body></html>
  `;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:none";
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument ?? iframe.contentWindow?.document;
  if (!doc) return;
  doc.open();
  doc.write(html);
  doc.close();
  iframe.contentWindow?.focus();
  iframe.contentWindow?.print();
  setTimeout(() => document.body.removeChild(iframe), 2000);
}
